/**
 * MH-22 Phát trực tiếp qua micro (mục 6.6 đặc tả giao diện).
 *
 * Ba phần: chọn phạm vi, kiểm tra thiết bị đầu vào (mức âm THẬT đọc từ micro
 * qua Web Audio API — bắt buộc trước khi mở phiên, để người dùng biết micro
 * đang hoạt động), và bảng điều khiển phiên phát. Sau khi mở phiên, nhúng
 * MH-23 để theo dõi — dùng chung đúng yêu cầu bắt buộc của mục 6.7.
 *
 * KHÔNG có điểm cuối truyền dòng âm thanh micro lên máy chủ trong 1.0.2 — bảng
 * điều khiển vẫn mở phiên thật (`POST /api/v1/broadcast/live/start`, ghi vào
 * `phien_phat_truc_tiep` và phát lệnh qua kênh phát) và đóng phiên thật, nhưng
 * KHÔNG truyền được âm thanh micro thật của người dùng đi — mục B4-13 của
 * NOTES-A0.md, cần A0/B3 bổ sung một kênh truyền dòng (WebRTC hoặc WebSocket
 * nhị phân) ở đợt sau. Màn hình nêu rõ giới hạn này ngay tại bảng điều khiển.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import { phatSongApi } from '../api/phat-song-api';
import { BoChonPhamViDiaBan, nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import { MH23TheoDoiPhien } from './MH23TheoDoiPhien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';

export interface MH22Props {
  api?: Pick<typeof phatSongApi, 'moPhienMicro' | 'dongPhienMicro'>;
}

export function MH22PhatTrucTiepMicro({ api }: MH22Props = {}): ReactNode {
  const goi = api ?? phatSongApi;
  const [buoc, datBuoc] = useState<1 | 2 | 3>(1);
  const phien = dungPhien();
  // Tài khoản cấp xã: phạm vi mặc định là xã được phân công.
  const [phamVi, datPhamVi] = useState<string[]>(() => (phien.khoaDiaBan ? [phien.khoaDiaBan.ma] : []));
  const [moPhamVi, datMoPhamVi] = useState(false);

  const [micLoi, datMicLoi] = useState<string | null>(null);
  const [mucAm, datMucAm] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const [phienId, datPhienId] = useState<string | null>(null);
  const [phienKenh, datPhienKenh] = useState<string | null>(null);
  const [dangMo, datDangMo] = useState(false);
  const [tatTieng, datTatTieng] = useState(false);
  const [batDauLuc, datBatDauLuc] = useState<number | null>(null);
  const [dangDong, datDangDong] = useState(false);
  const [thongBaoKetThuc, datThongBaoKetThuc] = useState(false);

  useEffect(() => {
    if (buoc !== 2) return undefined;
    let huy = false;
    void navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((stream) => {
        if (huy) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const ctx = new AudioContext();
        audioCtxRef.current = ctx;
        const nguon = ctx.createMediaStreamSource(stream);
        const phanTich = ctx.createAnalyser();
        phanTich.fftSize = 512;
        nguon.connect(phanTich);
        const du = new Uint8Array(phanTich.frequencyBinCount);
        const doc = (): void => {
          phanTich.getByteTimeDomainData(du);
          let tong = 0;
          for (const v of du) tong += (v - 128) * (v - 128);
          const rms = Math.sqrt(tong / du.length) / 128;
          datMucAm(Math.round(rms * 100));
          rafRef.current = requestAnimationFrame(doc);
        };
        doc();
      })
      .catch(() => datMicLoi(vi.phatSong.khongCoMic));
    return () => {
      huy = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      void audioCtxRef.current?.close();
    };
  }, [buoc]);

  const moPhien = async (): Promise<void> => {
    datDangMo(true);
    try {
      const kq = await goi.moPhienMicro(phamVi.map((ma) => ({ DichID: ma })));
      datPhienId(kq.phienId);
      datPhienKenh(kq.phienKenh);
      datBatDauLuc(Date.now());
      datBuoc(3);
    } finally {
      datDangMo(false);
    }
  };

  const ketThuc = async (): Promise<void> => {
    if (!phienId) return;
    datDangDong(true);
    try {
      await goi.dongPhienMicro(phienId);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      datThongBaoKetThuc(true);
    } finally {
      datDangDong(false);
    }
  };

  const giay = batDauLuc ? Math.floor((Date.now() - batDauLuc) / 1000) : 0;

  return (
    <div className="man-hinh" data-man-hinh="MH-22">
      <h1>{vi.phatSong.trucTiepTieuDe}</h1>
      <p data-vung="ghi-chu-ghi-am">{vi.phatSong.ghiChuLuonGhiAm}</p>

      {buoc === 1 ? (
        <section aria-label={vi.phatSong.buoc1ChonPhamViMicro}>
          <h2>{vi.phatSong.buoc1ChonPhamViMicro}</h2>
          <p>{phamVi.length === 0 ? vi.bienTap.chuaChonPhamVi : nhanPhamVi(phamVi, phien.tenDiaBanTheoMa)}</p>
          <button type="button" className="nut" onClick={() => datMoPhamVi(true)}>
            {vi.bienTap.chonPhamVi}
          </button>
          <button type="button" className="nut nut--chinh" disabled={phamVi.length === 0} onClick={() => datBuoc(2)}>
            {vi.phatSong.buoc2KiemTraMic}
          </button>
        </section>
      ) : null}

      {buoc === 2 ? (
        <section aria-label={vi.phatSong.buoc2KiemTraMic}>
          <h2>{vi.phatSong.buoc2KiemTraMic}</h2>
          {micLoi ? (
            <p role="alert">{micLoi}</p>
          ) : (
            <p data-vung="muc-am-thu-duoc">
              {vi.phatSong.mucAmThuDuoc}: <meter min={0} max={100} value={mucAm} aria-label={vi.phatSong.mucAmThuDuoc} /> {mucAm}
            </p>
          )}
          <button type="button" className="nut nut--chinh" disabled={!!micLoi} onClick={() => void moPhien()}>
            {dangMo ? vi.phatSong.dangMoPhien : vi.phatSong.moPhien}
          </button>
        </section>
      ) : null}

      {buoc === 3 && phienKenh ? (
        <section aria-label={vi.phatSong.buoc3BangDieuKhien}>
          <h2>{vi.phatSong.buoc3BangDieuKhien}</h2>
          <p data-vung="thoi-luong-phien">
            {vi.phatSong.thoiLuongPhien}: {Math.floor(giay / 60)}:{String(giay % 60).padStart(2, '0')}
          </p>
          <button type="button" className="nut" onClick={() => datTatTieng((x) => !x)}>
            {tatTieng ? vi.phatSong.batTiengLai : vi.phatSong.tatTiengTam}
          </button>
          <button type="button" className="nut nut--nguy-hiem" disabled={dangDong} onClick={() => void ketThuc()}>
            {vi.phatSong.ketThucPhien}
          </button>

          {thongBaoKetThuc ? <p role="status">{vi.phatSong.daKetThucPhien}</p> : null}

          {!thongBaoKetThuc ? <MH23TheoDoiPhien phienId={phienKenh} /> : null}
        </section>
      ) : null}

      {moPhamVi ? (
        <BoChonPhamViDiaBan
          giaTri={phamVi}
          onXacNhan={(ds) => {
            datPhamVi(ds);
            datMoPhamVi(false);
          }}
          onHuy={() => datMoPhamVi(false)}
        />
      ) : null}
    </div>
  );
}
